import { gql } from "@apollo/client";
import { WarehouseType } from "graphql/types/WarehouseType";

export const BAG_TAG_FIELDS = gql`
    fragment BagTagFields on GraphQLBagType {
        id
        bagNumber
        externalBagNumber
        bagType
        businessType
        printType
        fromLocation
        toLocation
        nextStop
        carrierCode
        warehouseId
        bagTaskId
        bagTaskName
        batchId
        batchNumber
        sortingCode
        sortingRule
        ttBizType
        sortingSegmentCode
        lastMileSortingCode
        status
        weightKg
        handoverVehicle
        handoverAt
        carrierCode
        sourceType
        sorterCode
        sortingChuteCodes
        createdAt
        creationType
        mergedToBagNumber
        externalBusinessType
        bagContainerId
        containerCode
        containerName
        containerTareWeightKg
        warehouse {
           id
           name
        }
    }
`;

/** 外部业务类型：0-普通，1-PPMOD(PPMOD，TEMU_SCF 子类) */
export enum ExternalBusinessType {
    NORMAL = 0,
    PPMOD = 1,
}

/** i18n key 前缀在调用点补齐，例：t(`wms.bags.externalBusinessType.${getExternalBusinessTypeKey(v)}`) */
export const getExternalBusinessTypeText = (
    v: number | undefined | null,
    t: (key: string) => string,
): string => {
    switch (v) {
        case ExternalBusinessType.PPMOD: return t('wms.bags.externalBusinessType.ppmod');
        case ExternalBusinessType.NORMAL: return t('wms.bags.externalBusinessType.normal');
        default: return '-';
    }
};

/** 业务类型（与后端 business_type 一致） */
export enum BusinessType {
    SWIFTX = 1,
    TT_SORT = 2,
    TEMU_SORT = 3,
}

export const getBusinessTypeText = (businessType: number | undefined): string => {
    switch (businessType) {
        case BusinessType.SWIFTX: return 'SWXLM';
        case BusinessType.TT_SORT: return 'TTSort';
        case BusinessType.TEMU_SORT: return 'TEMUSort';
        default: return '-';
    }
}

export const isTTBusinessType = (businessType: number | undefined): boolean =>
    businessType === BusinessType.TT_SORT;

/** 面单/打印模板类型：1-SWX 2-TT */
export const getPrintTypeText = (printType: number | undefined | null): string => {
    if (printType == null) return '-';
    switch (printType) {
        case 1: return 'SWX';
        case 2: return 'TT';
        default: return '-';
    }
};

export interface BagType {
    id?: number;
    bagNumber: string;
    externalBagNumber?: string;  // 外部包牌号
    bagType: number;  // 1-一段包牌、2-二段包牌、3-三段包牌、4-四段包牌
    businessType?: number;  // 业务类型：1-SwiftX、2-TT Sort、3-TEMU Sort
    printType?: number | null;  // 面单样式：1-SWX、2-TT
    fromLocation?: string;
    toLocation?: string;
    nextStop?: string;
    warehouseId: string;  // 创建站点ID
    bagTaskId?: number;  // 组包任务ID
    bagTaskName?: string;  // 组包任务名称
    batchId?: number;  // 批次ID
    batchNumber?: string;  // 批次号
    sortingCode: string;  // 段码(xx-yy-zz或xx-yy-zz-001)
    sortingRule?: string;  // 后端 computeSortingRule 结果：TT 为 from-nextStop-尾程商缩写；MIX/无尾程时缩写段为空
    ttBizType?: string | null;  // TT业务类型：CBT / FBT / UNLIMITED（bag.tt_biz_type）
    sortingSegmentCode?: string | null;  // 分拣业务段码（sorting_rule_first_sorting_rule_second，_ 拼接）
    lastMileSortingCode?: string | null;  // 尾程业务段码（bag.sorting_code）
    status: number;  // 状态：0-未使用(未装包扫描)、1-已使用(装包扫描)、2-已废弃(系统作废+拆包扫描作废)、3-已使用(尾程交接扫描)
    weightKg?: number | null;  // 装包重量（kg），null 表示未称重
    handoverVehicle?: string;  // 尾程交接车牌号
    handoverAt?: string;  // 尾程交接时间
    carrierCode?: number;  // 第三方末端承运商
    sourceType: number;  // 来源类型：0-手动创建，1-组包创建，2-交叉带创建，3-批次创建
    sorterCode?: string;  // 分拣机编码
    sortingChuteCodes?: string;  // 落格格口编码
    createdAt: string;  // 创建时间（unix timestamp秒数）
    creationType: string;  // 创建方式描述
    mergedToBagNumber?: string;  // 合包归属包牌号；源包牌指向 target、保留包牌指向自己（self-pointing），未参与合包为 null
    externalBusinessType?: number;  // 外部业务类型：0-普通，1-PP(PPMOD，TEMU_SCF 子类)
    bagContainerId?: number | null;  // 容器类型 id（关联 bag_container.id）；未选为 null
    containerCode?: number | null;   // 容器码（0-其他/1-LOW/2-HIGH/3-OTR-A/4-OTR-B）
    containerName?: string | null;   // 容器展示名（含尺寸）
    containerTareWeightKg?: number | null;  // 容器皮重（kg）；未绑定或未配置 tare 为 null，展示 Package Weight 时与 weightKg 相加
    warehouse: WarehouseType;  // 创建站点
}

export enum BagStatus {
    NOT_USED = 0,
    USED = 1,
    DISCARDED = 2,
    HANDOVER = 3,
    CLOSED = 4
}

export enum BagSourceType {
    SYSTEM_CREATED = 0,
    BAGGING_CREATED = 1,
    CROSS_BELT_CREATED = 2,
    BATCH_CREATED = 3,
    CLIENT_CARTON_SCAN_CREATED = 4,
    LINEAR_SORTER_CREATED = 5,
    SMALL_TRAIN_CREATED = 6
}

// 国际化版本的状态转换函数
export const getBagStatusText = (status: number, t: (key: string) => string): string => {
    switch (status) {
        case BagStatus.NOT_USED:
            return t('enum.bagStatus.notUsed');
        case BagStatus.USED:
            return t('enum.bagStatus.used');
        case BagStatus.DISCARDED:
            return t('enum.bagStatus.discarded');
        case BagStatus.HANDOVER:
            return t('enum.bagStatus.handover');
        case BagStatus.CLOSED:
            return t('enum.bagStatus.closed');
        default:
            return t('enum.bagStatus.unknown');
    }
}

// 保留原函数作为后备（向后兼容）
export const statusToText = (status: number) => {
    switch (status) {
        case BagStatus.NOT_USED:
            return 'Not used';
        case BagStatus.USED:
            return 'Used';
        case BagStatus.DISCARDED:
            return 'Discarded';
        case BagStatus.HANDOVER:
            return 'Handover';
        case BagStatus.CLOSED:
            return 'Closed';
        default:
            return 'Unknown';
    }
}

// 国际化版本的创建方式获取函数
export const getBagCreationType = (t: (key: string) => string, creationMethod?: number): string => {
    if (!creationMethod) {
        return t('wms.bags.creationMethod.manual');
    }
    switch (creationMethod) {
        case 1:
            return t('wms.bags.creationMethod.manual');
        case 2:
            return t('wms.bags.creationMethod.bagTask');
        case 3:
            return t('wms.bags.creationMethod.batch');
        default:
            return t('wms.bags.creationMethod.manual');
    }
}

// 保留原函数作为后备（向后兼容）
export const getCreationType = (bagTaskId?: number): string => {
    return bagTaskId ? '组包任务创建' : '手动创建';
}

// 来源类型文本转换函数
export const getBagSourceTypeText = (sourceType: number, t: (key: string) => string): string => {
    switch (sourceType) {
        case BagSourceType.SYSTEM_CREATED:
            return t('wms.bags.sourceType.system') || t('enum.bagSourceType.system') || '手动创建';
        case BagSourceType.BAGGING_CREATED:
            return t('wms.bags.sourceType.bagTask') || t('enum.bagSourceType.bagging') || '组包创建';
        case BagSourceType.CROSS_BELT_CREATED:
            return t('wms.bags.sourceType.crossBelt') || t('enum.bagSourceType.crossBelt') || '交叉带创建';
        case BagSourceType.BATCH_CREATED:
            return t('wms.bags.sourceType.batch') || '批次创建';
        case BagSourceType.CLIENT_CARTON_SCAN_CREATED:
            return t('wms.bags.sourceType.clientCarton') || '客户大包扫描';
        case BagSourceType.LINEAR_SORTER_CREATED:
            return t('wms.bags.sourceType.linearSorter') || '直线分拣机创建';
        case BagSourceType.SMALL_TRAIN_CREATED:
            return t('wms.bags.sourceType.smallTrain') || '小火车创建';
        default:
            return t('enum.bagSourceType.unknown') || '未知';
    }
}

export interface BagTypeInput {
    id?: number;
    bagNumber: string;
    bagType: number;  // 1-一段包牌、2-二段包牌、3-三段包牌、4-四段包牌
    warehouseId: number;  // 创建站点ID
    sortingCode: string;  // 段码(xx-yy-zz或xx-yy-zz-001)
    status: number;  // 状态：0-未使用(未装包扫描)、1-已使用(装包扫描)、2-已废弃(系统作废+拆包扫描作废)、3-已使用(尾程交接扫描)
}